import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import TeamCreationForm from '../components/TeamCreationForm';
import TeamJoinForm from '../components/TeamJoinForm';
import TeamInviteForm from '../components/TeamInviteForm';

const TeamManagement = () => {
  const { user } = useAuth();
  const [confirmState, setConfirmState] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDetails, setShowTeamDetails] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [defaultOrganizationId, setDefaultOrganizationId] = useState(null);
  const [editingTeam, setEditingTeam] = useState(false);
  const [editTeamForm, setEditTeamForm] = useState({});
  const [savingTeam, setSavingTeam] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState([]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (user && !organizationsLoading) {
      fetchTeams();
    }
  }, [user, organizationsLoading, organizations]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      console.log('Fetching teams with token:', token ? 'present' : 'missing');
      const myTeamsResponse = await axios.get('/api/teams/my-teams', {
        headers
      });

      let combinedTeams = Array.isArray(myTeamsResponse.data) ? [...myTeamsResponse.data] : [];
      console.log('User teams fetched:', combinedTeams);
      console.log('Number of teams:', combinedTeams.length);

      const hasOrgPrivileges = ['admin', 'org_admin', 'app_manager'].includes(user?.role);
      if (hasOrgPrivileges && organizations.length > 0) {
        const existingTeamIds = new Set(combinedTeams.map(team => team.id));

        for (const org of organizations) {
          if (!org?.id) continue;

          try {
            console.log('Fetching organization teams for org:', org.id);
            const orgTeamsResponse = await axios.get('/api/teams', {
              params: { organizationId: org.id },
              headers
            });

            const orgTeams = Array.isArray(orgTeamsResponse.data) ? orgTeamsResponse.data : [];
            orgTeams.forEach(team => {
              if (!existingTeamIds.has(team.id)) {
                combinedTeams.push(team);
                existingTeamIds.add(team.id);
              }
            });
          } catch (orgError) {
            console.error(`Error fetching teams for organization ${org.id}:`, orgError);
          }
        }
      }

      if (hasOrgPrivileges && organizations.length === 0) {
        try {
          console.log('No organizations linked; fetching all teams for admin role.');
          const allTeamsResponse = await axios.get('/api/teams', { headers });
          const allTeams = Array.isArray(allTeamsResponse.data) ? allTeamsResponse.data : [];
          const existingTeamIds = new Set(combinedTeams.map(team => team.id));

          allTeams.forEach(team => {
            if (!existingTeamIds.has(team.id)) {
              combinedTeams.push(team);
              existingTeamIds.add(team.id);
            }
          });
        } catch (allTeamsError) {
          console.error('Error fetching all teams for admin:', allTeamsError);
        }
      }

      setTeams(combinedTeams);
      console.log('Combined team list:', combinedTeams);
    } catch (error) {
      console.error('Error fetching user teams:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to fetch your teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      const response = await axios.get('/api/organizations/my-organizations');
      const orgUsers = Array.isArray(response.data) ? response.data : [];

      const transformed = orgUsers.map(orgUser => {
        if (orgUser.Organization) {
          return {
            id: orgUser.Organization.id,
            name: orgUser.Organization.name,
            description: orgUser.Organization.description,
            organizationCode: orgUser.Organization.organizationCode,
            role: orgUser.role,
            isPrimary: orgUser.is_primary
          };
        }

        if (orgUser.id && orgUser.name) {
          return {
            id: orgUser.id,
            name: orgUser.name,
            description: orgUser.description,
            organizationCode: orgUser.organizationCode,
            role: orgUser.role,
            isPrimary: orgUser.is_primary
          };
        }

        return {
          id: orgUser.organization_id || null,
          name: orgUser.name || 'Unknown Organization',
          description: orgUser.description || '',
          organizationCode: orgUser.organizationCode || '',
          role: orgUser.role,
          isPrimary: orgUser.is_primary
        };
      }).filter(org => !!org.id);

      setOrganizations(transformed);

      if (!defaultOrganizationId && transformed.length > 0) {
        const primaryOrg = transformed.find(org => org.isPrimary || ['owner', 'admin', 'coach'].includes(org.role));
        setDefaultOrganizationId((primaryOrg || transformed[0]).id);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch organizations');
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const fetchProgramsForOrg = async (orgId) => {
    if (!orgId) { setAvailablePrograms([]); return; }
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`/api/programs?organization_id=${orgId}&active=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAvailablePrograms(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setAvailablePrograms([]);
    }
  };

  const handleTeamCreated = (newTeam) => {
    setTeams(prev => [newTeam, ...prev]);
    setShowCreateForm(false);
  };

  const handleTeamJoined = async (team) => {
    console.log('Team joined successfully:', team);
    setShowJoinForm(false);
    toast.success(`Successfully joined ${team.name}!`);
    
    // Force refresh the team list
    await fetchTeams();
  };

  const handleInviteSent = (inviteData) => {
    console.log('Invitation sent successfully:', inviteData);
    setShowInviteForm(false);
    setSelectedTeam(null);
  };

  const handleDeactivateTeam = (teamId) => {
    setConfirmState({
      title: 'Deactivate Team',
      message: 'Are you sure you want to deactivate this team? This action cannot be undone.',
      isDangerous: true,
      confirmLabel: 'Deactivate',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.delete(`/api/teams/${teamId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          console.log('Delete response:', response.data);
          toast.success('Team deactivated successfully');
          fetchTeams();
        } catch (error) {
          console.error('Error deactivating team:', error);
          console.error('Error response:', error.response?.data);
          console.error('Error status:', error.response?.status);
          const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to deactivate team';
          toast.error(errorMessage);
        }
      },
    });
  };

  const copyJoinCode = (teamCode) => {
    navigator.clipboard.writeText(teamCode);
    toast.success('Join code copied to clipboard!');
  };

  const handleViewTeam = async (team) => {
    setSelectedTeam(team);
    setEditingTeam(false);
    setShowTeamDetails(true);
    await fetchTeamMembers(team.id);
  };

  const handleEditTeamFromCard = async (team) => {
    setSelectedTeam(team);
    setShowTeamDetails(true);
    await fetchTeamMembers(team.id);
    await fetchProgramsForOrg(team.organization_id || team.organizationId);
    setEditTeamForm({
      name: team.name || '',
      sport: team.sport || 'baseball',
      ageGroup: team.ageGroup || '',
      season: team.season || '',
      description: team.description || '',
      maxPlayers: team.maxPlayers ?? 20,
      primaryColor: (team.colors && team.colors.primary) || '#2563eb',
      secondaryColor: (team.colors && team.colors.secondary) || '#1d4ed8',
      program_id: team.program_id || '',
      division: team.division || ''
    });
    setEditingTeam(true);
  };

  const handleStartEditTeam = () => {
    if (!selectedTeam) return;
    fetchProgramsForOrg(selectedTeam.organization_id || selectedTeam.organizationId);
    setEditTeamForm({
      name: selectedTeam.name || '',
      sport: selectedTeam.sport || 'baseball',
      ageGroup: selectedTeam.ageGroup || '',
      season: selectedTeam.season || '',
      description: selectedTeam.description || '',
      maxPlayers: selectedTeam.maxPlayers ?? 20,
      primaryColor: (selectedTeam.colors && selectedTeam.colors.primary) || '#2563eb',
      secondaryColor: (selectedTeam.colors && selectedTeam.colors.secondary) || '#1d4ed8',
      program_id: selectedTeam.program_id || '',
      division: selectedTeam.division || ''
    });
    setEditingTeam(true);
  };

  const handleCancelEditTeam = () => {
    setEditingTeam(false);
    setEditTeamForm({});
  };

  const handleSaveTeam = async () => {
    if (!selectedTeam || !editTeamForm.name?.trim()) {
      toast.error('Team name is required');
      return;
    }
    try {
      setSavingTeam(true);
      const token = localStorage.getItem('token');
      const payload = {
        name: editTeamForm.name.trim(),
        sport: editTeamForm.sport || 'baseball',
        ageGroup: editTeamForm.ageGroup || null,
        season: editTeamForm.season || null,
        description: editTeamForm.description || null,
        maxPlayers: Math.max(1, Math.min(50, parseInt(editTeamForm.maxPlayers, 10) || 20)),
        program_id: editTeamForm.program_id ? parseInt(editTeamForm.program_id, 10) : null,
        division: editTeamForm.division?.trim() || null,
        colors: {
          primary: editTeamForm.primaryColor || '#2563eb',
          secondary: editTeamForm.secondaryColor || '#1d4ed8'
        }
      };
      const response = await axios.put(`/api/teams/${selectedTeam.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updated = response.data;
      setSelectedTeam(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
      setTeams(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
      setEditingTeam(false);
      setEditTeamForm({});
      toast.success('Team updated successfully');
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to update team');
    } finally {
      setSavingTeam(false);
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      setLoadingMembers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/user-teams/team/${teamId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Team members:', response.data);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
      setTeamMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleEmailTeam = (team, recipients = null) => {
    setSelectedTeam(team);
    setEmailSubject('');
    setEmailMessage('');
    setEmailRecipients(recipients); // null = entire team, otherwise array of member IDs
    setShowEmailModal(true);
  };

  const handleEmailMember = (team, member) => {
    setSelectedTeam(team);
    setEmailSubject('');
    setEmailMessage('');
    setEmailRecipients([member.user_id]);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Please enter both subject and message');
      return;
    }

    try {
      setSendingEmail(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        teamId: selectedTeam.id,
        subject: emailSubject,
        message: emailMessage
      };

      // If specific recipients are selected, add them to the payload
      if (emailRecipients && emailRecipients.length > 0) {
        payload.recipientIds = emailRecipients;
      }
      
      await axios.post('/api/emails/send-team-email', payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const recipientText = emailRecipients && emailRecipients.length > 0 
        ? `${emailRecipients.length} team member${emailRecipients.length !== 1 ? 's' : ''}`
        : 'all team members';
      
      toast.success(`Email sent successfully to ${recipientText}!`);
      setShowEmailModal(false);
      setEmailSubject('');
      setEmailMessage('');
      setEmailRecipients([]);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <TeamCreationForm 
            onTeamCreated={handleTeamCreated}
            onCancel={() => setShowCreateForm(false)}
            defaultOrganizationId={defaultOrganizationId}
            organizations={organizations}
          />
        </div>
      </div>
    );
  }

  if (showJoinForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <TeamJoinForm 
            onTeamJoined={handleTeamJoined}
            onCancel={() => setShowJoinForm(false)}
          />
        </div>
      </div>
    );
  }

  if (showInviteForm && selectedTeam) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <TeamInviteForm 
            team={selectedTeam}
            onInviteSent={handleInviteSent}
            onCancel={() => {
              setShowInviteForm(false);
              setSelectedTeam(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Debug Info */}
        {user && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Logged in as:</strong> {user.email} | <strong>Role:</strong> {user.role} | <strong>User ID:</strong> {user.id}
            </p>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
            <p className="text-gray-600 mt-2">Create and manage teams for your organization</p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => setShowJoinForm(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Join Team
            </button>
            {(user.role === 'admin' || user.role === 'org_admin' || user.role === 'coach') && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Create Team
              </button>
            )}
          </div>
        </div>

        {/* Teams Grid */}
        {teams.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams found</h3>
            <p className="mt-1 text-sm text-gray-500">
              You are not currently a member of any teams.
            </p>
            <div className="mt-6 space-y-3">
              {(user.role === 'admin' || user.role === 'org_admin' || user.role === 'coach') && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Team
                </button>
              )}
              <div>
                <button
                  onClick={() => setShowJoinForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Join Team
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => (
              <div key={team.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Team Header */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: team.colors?.primary || '#2563eb' }}
                      >
                        {team.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
                        <p className="text-sm text-gray-500">{team.sport} • {team.ageGroup} • ID: {team.id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {['admin', 'org_admin', 'coach', 'assistant_coach'].includes(user.role) && (
                        <button
                          onClick={() => handleEditTeamFromCard(team)}
                          className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                          title="Edit team"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      {(user.role === 'admin' || user.role === 'org_admin' || user.role === 'coach') && (
                        <button
                          onClick={() => handleDeactivateTeam(team.id)}
                          className="text-red-400 hover:text-red-600 transition-colors p-1"
                          title="Deactivate team"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Team Details */}
                  <div className="space-y-2 mb-4">
                    {team.season && (
                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {team.season}
                      </div>
                    )}
                    {team.program && (
                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <span>{team.program.name}{team.program.season ? ` — ${team.program.season}` : ''}</span>
                        {!team.program.is_active && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Archived</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {team.currentPlayers} / {team.maxPlayers} players
                    </div>
                  </div>

                  {/* Join Code Section */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Join Code</span>
                      <button
                        onClick={() => copyJoinCode(team.teamCode)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-center font-mono text-lg tracking-wider">
                        {team.teamCode}
                      </code>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Share this code with players and parents to join the team
                    </p>
                  </div>

                  {/* Team Description */}
                  {team.description && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 line-clamp-2">{team.description}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {['admin', 'org_admin', 'coach', 'assistant_coach'].includes(user.role) && (
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => handleViewTeam(team)}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Manage
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTeam(team);
                          setShowInviteForm(true);
                        }}
                        className="flex-1 px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        Invite
                      </button>
                      <button
                        onClick={() => handleEmailTeam(team)}
                        className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email
                      </button>
                    </div>
                  )}
                </div>

                {/* Team Footer */}
                <div className="bg-gray-50 px-6 py-3 border-t">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Created {new Date(team.createdAt).toLocaleDateString()}</span>
                    <span className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${team.isActive ? 'bg-green-400' : 'bg-red-400'}`}></div>
                      {team.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Team Details Modal */}
        {showTeamDetails && selectedTeam && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">{editingTeam ? 'Edit Team' : selectedTeam.name}</h2>
                <div className="flex items-center gap-2">
                  {!editingTeam && ['admin', 'org_admin', 'coach', 'assistant_coach'].includes(user?.role) && (
                    <button
                      onClick={handleStartEditTeam}
                      className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => { setShowTeamDetails(false); setEditingTeam(false); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {editingTeam ? (
                  /* Edit Team Form */
                  <div className="space-y-4 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Team Name *</label>
                      <input
                        type="text"
                        value={editTeamForm.name || ''}
                        onChange={(e) => setEditTeamForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Team name"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sport</label>
                        <input
                          type="text"
                          value={editTeamForm.sport || ''}
                          onChange={(e) => setEditTeamForm(f => ({ ...f, sport: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. baseball"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
                        <input
                          type="text"
                          value={editTeamForm.ageGroup || ''}
                          onChange={(e) => setEditTeamForm(f => ({ ...f, ageGroup: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. 10U"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
                        <input
                          type="text"
                          value={editTeamForm.season || ''}
                          onChange={(e) => setEditTeamForm(f => ({ ...f, season: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. Spring 2025"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Players</label>
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={editTeamForm.maxPlayers ?? 20}
                          onChange={(e) => setEditTeamForm(f => ({ ...f, maxPlayers: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Division (optional)</label>
                      <input
                        type="text"
                        value={editTeamForm.division || ''}
                        onChange={(e) => setEditTeamForm(f => ({ ...f, division: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. American, National"
                      />
                      <p className="text-xs text-gray-500 mt-1">Sub-groups teams within an age group into separate round-robin/standings pools. Leave blank for one pool per age group.</p>
                    </div>
                    {availablePrograms.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
                        <select
                          value={editTeamForm.program_id || ''}
                          onChange={(e) => setEditTeamForm(f => ({ ...f, program_id: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">No program</option>
                          {availablePrograms.map(p => (
                            <option key={p.id} value={p.id}>{p.name}{p.season ? ` — ${p.season}` : ''}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editTeamForm.description || ''}
                        onChange={(e) => setEditTeamForm(f => ({ ...f, description: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Optional team description"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={editTeamForm.primaryColor || '#2563eb'}
                            onChange={(e) => setEditTeamForm(f => ({ ...f, primaryColor: e.target.value }))}
                            className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={editTeamForm.primaryColor || '#2563eb'}
                            onChange={(e) => setEditTeamForm(f => ({ ...f, primaryColor: e.target.value }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={editTeamForm.secondaryColor || '#1d4ed8'}
                            onChange={(e) => setEditTeamForm(f => ({ ...f, secondaryColor: e.target.value }))}
                            className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={editTeamForm.secondaryColor || '#1d4ed8'}
                            onChange={(e) => setEditTeamForm(f => ({ ...f, secondaryColor: e.target.value }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <button
                        onClick={handleCancelEditTeam}
                        disabled={savingTeam}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveTeam}
                        disabled={savingTeam || !editTeamForm.name?.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {savingTeam ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                {/* Team Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                    <p className="text-gray-900">{selectedTeam.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sport</label>
                    <p className="text-gray-900">{selectedTeam.sport}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
                    <p className="text-gray-900">{selectedTeam.ageGroup || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
                    <p className="text-gray-900">{selectedTeam.season || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Team Code</label>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-3 py-1 rounded font-mono">{selectedTeam.teamCode}</code>
                      <button
                        onClick={() => copyJoinCode(selectedTeam.teamCode)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Players</label>
                    <p className="text-gray-900">{selectedTeam.currentPlayers} / {selectedTeam.maxPlayers}</p>
                  </div>
                </div>

                {/* Description */}
                {selectedTeam.description && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <p className="text-gray-900">{selectedTeam.description}</p>
                  </div>
                )}

                {/* Team Colors */}
                {selectedTeam.colors && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Team Colors</label>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-12 h-12 rounded border-2 border-gray-300"
                          style={{ backgroundColor: selectedTeam.colors.primary }}
                        />
                        <span className="text-sm text-gray-600">Primary: {selectedTeam.colors.primary}</span>
                      </div>
                      {selectedTeam.colors.secondary && (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-12 h-12 rounded border-2 border-gray-300"
                            style={{ backgroundColor: selectedTeam.colors.secondary }}
                          />
                          <span className="text-sm text-gray-600">Secondary: {selectedTeam.colors.secondary}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Team Roster */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Team Roster ({teamMembers.length} members)</label>
                    <button
                      onClick={() => handleEmailTeam(selectedTeam)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email All
                    </button>
                  </div>
                  
                  {loadingMembers ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 mt-2">Loading members...</p>
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="text-sm text-gray-600">No team members yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {teamMembers.map((member) => (
                        <div 
                          key={member.id} 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-700 font-semibold text-sm">
                                {member.User?.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{member.User?.name || 'Unknown'}</p>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="capitalize">{member.relationship_type?.replace('_', ' ')}</span>
                                {member.User?.email && (
                                  <>
                                    <span>•</span>
                                    <span className="text-xs">{member.User.email}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleEmailMember(selectedTeam, member)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Email this member"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => setShowTeamDetails(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowTeamDetails(false);
                      handleEmailTeam(selectedTeam);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Email Team
                  </button>
                </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Email Team Modal */}
        {showEmailModal && selectedTeam && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
              <div className="border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Email Team: {selectedTeam.name}</h2>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={sendingEmail}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sendingEmail}
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Enter your message to the team..."
                    rows="8"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sendingEmail}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-900">
                    <strong>Recipients:</strong> {emailRecipients && emailRecipients.length > 0 ? (
                      <>
                        {emailRecipients.length} selected team member{emailRecipients.length !== 1 ? 's' : ''}
                        <span className="block text-xs mt-1 text-blue-700">
                          {teamMembers
                            .filter(m => emailRecipients.includes(m.user_id))
                            .map(m => m.User?.name)
                            .join(', ')}
                        </span>
                      </>
                    ) : (
                      'All team members with email notifications enabled'
                    )}
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={sendingEmail}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {sendingEmail ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default TeamManagement;